import {
  Battery,
  Clock,
  DollarSign,
  HardHat,
  Leaf,
  MapPin,
  Shield,
  Smartphone,
  Zap,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import "swiper/css";
import "swiper/css/navigation";
import { JSX, useEffect, useState } from "react";
import * as HiIcons from "react-icons/hi";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import appGalleryIcon from "@/assets/apps/appgallery.png";
import appStoreIcon from "@/assets/apps/appstore.png";
import playStoreIcon from "@/assets/apps/playstore.jpg";
import webIcon from "@/assets/apps/web.jfif";
import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { fetchAppDownloadSection } from "@/services/landing/hareengEatsService";
import { fetchHeroBackgrounds } from "@/services/landing/heroBackgroundService";
import {
  fetchBenefits,
  fetchFees,
  fetchSafetyTips,
} from "@/services/landing/scooterService";
import { fetchScooters } from "@/services/landing/scooterService";
import { fetchSectionConfig } from "@/services/landing/scooterService";

export default function Scooters() {
  // Scooters state
  const [scooters, setScooters] = useState<any[]>([]);
  const [scootersLoading, setScootersLoading] = useState(true);
  const [heroBackground, setHeroBackground] = useState<null | {
    imageAlt: string;
    imageUrl: string;
  }>(null);
  const [heroLoading, setHeroLoading] = useState(true);

  // App Download URLs state
  const [androidUrl, setAndroidUrl] = useState<string>("");
  const [iosUrl, setIosUrl] = useState<string>("");
  const [webUrl, setWebUrl] = useState<string>("");
  const [appGalleryUrl, setAppGalleryUrl] = useState<string>("");

  useEffect(() => {
    Promise.all([
      fetchAppDownloadSection(),
      fetchHeroBackgrounds("scooters"),
      fetchScooters(),
    ]).then(([appSection, heroData, scootersData]) => {
      if (appSection) {
        setAndroidUrl(appSection.androidUrl || "");
        setIosUrl(appSection.iosUrl || "");
        setWebUrl(appSection.webUrl || "");
        setAppGalleryUrl(appSection.appGalleryUrl || "");
      }
      if (heroData) {
        setHeroBackground(heroData);
        setHeroLoading(false);
      }
      if (scootersData) {
        setScooters(scootersData);
        setScootersLoading(false);
      }
    });
  }, []);
  // Safety section config state
  const [safetyConfig, setSafetyConfig] = useState<any>(null);
  const [safetyConfigLoading, setSafetyConfigLoading] = useState(true);

  useEffect(() => {
    fetchSectionConfig().then((data) => {
      setSafetyConfig(data);
      setSafetyConfigLoading(false);
    });
  }, []);
  // Scooter safety tips state (must be inside component)
  const [safetyTips, setSafetyTips] = useState<any[]>([]);
  const [safetyTipsLoading, setSafetyTipsLoading] = useState(true);

  useEffect(() => {
    fetchSafetyTips().then((data) => {
      setSafetyTips(data);
      setSafetyTipsLoading(false);
    });
  }, []);
  const steps = [
    {
      description: "Use the app to locate nearby scooters",
      icon: <Smartphone className="text-primary" size={32} />,
      step: "1",
      title: "Find a Scooter",
    },
    {
      description: "Unlock instantly with your phone",
      icon: <Zap className="text-secondary" size={32} />,
      step: "2",
      title: "Place Order",
    },
    {
      description: "Wear helmet and follow riders rules",
      icon: <HardHat className="text-accent" size={32} />,
      step: "3",
      title: "Ride Safely",
    },
    {
      description: "End ride at any designated parking spot",
      icon: <MapPin className="text-success" size={32} />,
      step: "4",
      title: "Enjoy your journey",
    },
  ];

  // Scooter benefits state
  const [benefits, setBenefits] = useState<any[]>([]);
  const [benefitsLoading, setBenefitsLoading] = useState(true);

  useEffect(() => {
    fetchBenefits("").then((data) => {
      setBenefits(data);
      setBenefitsLoading(false);
    });
  }, []);

  const [showQR, setShowQR] = useState(false);
  const [qrPlatform, setQrPlatform] = useState<"android" | "appgallery" | "ios" | "web">("android");
  const appDownloadUrl = "https://your-app-download-link.com"; // Replace with your actual app link

  // Scooter fees state
  const [fees, setFees] = useState<null | {
    price_per_minute: string;
    unlock_fee: string;
  }>(null);
  const [feesLoading, setFeesLoading] = useState(true);

  useEffect(() => {
    fetchFees().then((data) => {
      setFees(
        data
          ? {
              price_per_minute: data.price_per_minute,
              unlock_fee: data.unlock_fee,
            }
          : null,
      );
      setFeesLoading(false);
    });
  }, []);

  // Theme adaptive QR colors
  const [isDark, setIsDark] = useState(
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributeFilter: ["class"],
      attributes: true,
    });
    return () => observer.disconnect();
  }, []);

  if (
    typeof document !== "undefined" &&
    !document.getElementById("hero-shimmer-style")
  ) {
    const style = document.createElement("style");
    style.id = "hero-shimmer-style";
    style.innerHTML = `
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .animate-shimmer {
        animation: shimmer 2s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }

  return (
    <>
      <AppNavbar />

      <div className="min-h-screen bg-base-200">
        <Breadcrumbs />

        {/* Hero Section */}
        <section className="relative -mt-16 overflow-hidden pt-16">
          <div className="absolute inset-0 opacity-30">
            {heroLoading ? (
              <div className="relative h-full w-full overflow-hidden bg-base-200">
                <div
                  className="animate-shimmer absolute inset-0 bg-gradient-to-r from-base-200 via-base-100 to-base-200"
                  style={{ backgroundSize: "200% 100%" }}
                />
              </div>
            ) : heroBackground?.imageUrl ? (
              <img
                alt={heroBackground?.imageAlt || "Hero background"}
                className="h-full w-full object-cover"
                src={heroBackground.imageUrl}
              />
            ) : null}
          </div>

          <div className="relative mx-auto max-w-7xl px-4 py-24">
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div>
                <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-success-content/20 px-4 py-2">
                  <Leaf size={20} />
                  <span className="font-medium">
                    100% Electric • Zero Emissions
                  </span>
                </div>

                <h1 className="mb-6 text-5xl font-bold leading-tight lg:text-6xl">
                  Zip around the city with{" "}
                  <span className="text-primary">Scooters</span>
                </h1>

                <p className="mb-8 text-xl opacity-90">
                  Fast, fun, and eco-friendly. Perfect for short trips, beating
                  traffic, and reducing your carbon footprint.
                </p>

                <div className="flex flex-col gap-4 sm:flex-row">
                  <a
                    className="btn btn-outline btn-lg"
                    href="/driver/scooter-driver-signup"
                  >
                    Become a Driver
                  </a>
                </div>
              </div>

              <div className="relative">
                <div
                  className="rounded-2xl p-8 shadow-2xl"
                  style={{
                    backgroundColor: isDark ? "#1e293b" : "#e6f9f0",
                    color: isDark ? "#22c55e" : "#059669",
                  }}
                >
                  <div className="mb-6 text-center">
                    <Zap
                      className="mx-auto mb-4"
                      size={48}
                      style={{ color: isDark ? "#22c55e" : "#059669" }}
                    />
                    <h3 className="text-2xl font-bold">Start Your Ride</h3>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span>Unlock Fee</span>
                      <span className="font-bold">
                        {feesLoading
                          ? "..."
                          : fees
                            ? `R${fees.unlock_fee}`
                            : "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Per Minute</span>
                      <span className="font-bold">
                        {feesLoading
                          ? "..."
                          : fees
                            ? `R${fees.price_per_minute}`
                            : "N/A"}
                      </span>
                    </div>
                    <div
                      className="divider"
                      style={{ background: isDark ? "#334155" : "#bbf7d0" }}
                    ></div>
                    <div className="flex items-center justify-between text-lg font-bold">
                      <span>10 min ride</span>
                      <span>
                        {feesLoading
                          ? "..."
                          : fees
                            ? `R${(
                                parseFloat(fees.unlock_fee) +
                                10 * parseFloat(fees.price_per_minute)
                              ).toFixed(2)}`
                            : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">How It Works</h2>
              <p className="text-xl text-base-content/70">
                Four simple steps to start scooting
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {steps.map((step, index) => (
                <div className="text-center" key={index}>
                  <div className="relative mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary text-primary-content">
                    <span className="text-2xl font-bold">{step.step}</span>
                    <div className="absolute -bottom-2 -right-2 rounded-full bg-base-100 p-2">
                      {step.icon}
                    </div>
                  </div>
                  <h3 className="mb-2 text-xl font-semibold">{step.title}</h3>
                  <p className="text-base-content/70">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Scooter Image Gallery */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Our Fleet</h2>
              <p className="text-xl text-base-content/70">
                Modern, safe, and reliable scooters
              </p>
            </div>

            {scootersLoading ? (
              <div className="text-center">Loading...</div>
            ) : scooters.length > 3 ? (
              <Swiper
                breakpoints={{
                  0: { slidesPerView: 1 },
                  640: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
                className="!pb-10"
                modules={[Navigation]}
                navigation
                slidesPerView={1}
                spaceBetween={24}
              >
                {scooters.map((scooter) => (
                  <SwiperSlide key={scooter.id}>
                    <div className="card bg-base-200 shadow-lg">
                      <figure className="h-48">
                        <img
                          alt={scooter.name}
                          className="h-full w-full object-cover"
                          src={
                            scooter.image_path
                              ? `${import.meta.env.VITE_API_STORAGE_URL}/storage/${scooter.image_path}`
                              : undefined
                          }
                        />
                      </figure>
                      <div className="card-body">
                        <div className="mb-1 flex items-center justify-between">
                          <h3 className="card-title m-0">{scooter.name}</h3>
                          <span className="whitespace-nowrap text-lg font-bold text-primary">
                            From R
                            {parseFloat(scooter.price_per_hour).toFixed(2)}
                          </span>
                        </div>
                        <p>{scooter.description}</p>
                        <ul className="mt-2 space-y-1 text-sm">
                          <li className="flex justify-between">
                            <span className="font-semibold">Features:</span>
                            <span className="text-right">
                              {scooter.features}
                            </span>
                          </li>
                          <li className="flex justify-between">
                            <span className="font-semibold">Battery:</span>
                            <span className="text-right">
                              {scooter.battery_level}%
                            </span>
                          </li>
                          <li className="flex justify-between">
                            <span className="font-semibold">Ready Time:</span>
                            <span className="text-right">
                              {scooter.ready_time}
                            </span>
                          </li>
                          <li className="flex justify-between">
                            <span className="font-semibold">Location:</span>
                            <span className="text-right">
                              {scooter.location}
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <div className="grid gap-6 grid-cols-2 md:grid-cols-3">
                {scooters.map((scooter) => (
                  <div className="card bg-base-200 shadow-lg" key={scooter.id}>
                    <figure className="h-48">
                      <img
                        alt={scooter.name}
                        className="h-full w-full object-cover"
                        src={
                          scooter.image_path
                            ? `${import.meta.env.VITE_API_STORAGE_URL}/storage/${scooter.image_path}`
                            : undefined
                        }
                      />
                    </figure>
                    <div className="card-body">
                      <h3 className="card-title">{scooter.name}</h3>
                      <p>{scooter.description}</p>
                      <div className="flex items-center gap-2 text-sm">
                        <span>{scooter.features}</span>
                      </div>
                      <div className="mt-2 font-bold text-primary">
                        R{parseFloat(scooter.price_per_hour).toFixed(2)} / hour
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">
                Why Choose Electric Scooters?
              </h2>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {benefitsLoading ? (
                <div className="col-span-4 text-center">Loading...</div>
              ) : (
                benefits.map((benefit, index) => {
                  // Dynamically resolve icon from benefit.icon string (e.g., "HiStar")
                  const IconTag =
                    (HiIcons as any)[benefit.icon] || HiIcons.HiStar;
                  return (
                    <div
                      className="card bg-base-100 shadow-lg"
                      key={benefit.id || index}
                    >
                      <div className="card-body text-center">
                        <div className="mx-auto mb-4">
                          <IconTag
                            className={`text-${benefit.color || "primary"}`}
                            size={32}
                          />
                        </div>
                        <h3 className="card-title justify-center">
                          {benefit.title}
                        </h3>
                        <p className="text-base-content/70">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </section>

        {/* Safety Section */}
        <section className="bg-warning/10 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div>
                <h2 className="mb-6 text-4xl font-bold">
                  {safetyConfigLoading
                    ? "Safety First"
                    : safetyConfig?.section_title || "Safety First"}
                </h2>
                <div className="space-y-4">
                  {safetyTipsLoading ? (
                    <div>Loading...</div>
                  ) : (
                    safetyTips.map((tip, idx) => {
                      // Support all availableIcons
                      const iconName =
                        tip.icon === "HiExclamationTriangle"
                          ? "HiExclamationCircle"
                          : tip.icon;
                      const IconTag =
                        (HiIcons as any)[iconName as string] ||
                        HiIcons.HiShieldCheck;
                      return (
                        <div
                          className="flex items-start gap-3"
                          key={tip.id || idx}
                        >
                          <IconTag
                            className={`mt-1 text-${tip.color || "primary"}`}
                            size={24}
                          />
                          <div>
                            <h3 className="font-semibold">{tip.title}</h3>
                            <p className="text-base-content/70">
                              {tip.description}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="rounded-2xl bg-base-100 p-8 shadow-xl">
                {safetyConfigLoading ? (
                  <div className="h-64 w-full animate-pulse rounded-lg bg-base-200" />
                ) : (
                  <img
                    alt={safetyConfig?.image_alt || "Safe scooter riding"}
                    className="h-64 w-full rounded-lg object-cover"
                    src={
                      safetyConfig?.image_path
                        ? `${import.meta.env.VITE_API_STORAGE_URL}/storage/${safetyConfig.image_path}`
                        : undefined
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h2 className="mb-4 text-4xl font-bold">Start Scooting Today!</h2>
            <p className="mb-8 text-xl opacity-90">
              Join thousands of riders choosing sustainable transport
            </p>
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
              <a
                className="btn btn-outline btn-lg"
                href="/driver/scooter-driver-signup"
              >
                Become a Driver
              </a>
            </div>
          </div>
        </section>

        {showQR && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="relative flex w-[340px] flex-col items-center rounded-2xl bg-white p-10 text-center shadow-2xl dark:bg-base-200 sm:w-[400px]">
              <button
                aria-label="Close"
                className="absolute right-6 top-6 text-3xl font-bold text-gray-500 transition-colors hover:text-primary dark:text-gray-300"
                onClick={() => setShowQR(false)}
                style={{ lineHeight: 1 }}
              >
                ×
              </button>
              <h3 className="mb-6 text-2xl font-bold text-primary dark:text-accent">
                Scan to Download Our App
              </h3>
              <div className="mb-6 flex items-center justify-center">
                <QRCodeCanvas
                  bgColor={isDark ? "#1e293b" : "#ffffff"}
                  fgColor={isDark ? "#f3f4f6" : "#1f2937"}
                  size={220}
                  style={{
                    background: isDark ? "#1e293b" : "#fff",
                    borderRadius: "16px",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
                    padding: "12px",
                  }}
                  value={appDownloadUrl}
                />
              </div>
              <p className="text-base text-gray-600 dark:text-gray-300">
                Scan this QR code with your phone to download the app.
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
